import { Middleware } from "redux";
import {
  AppState,
  ReportFeedbackAction,
  REPORT_APP_FEEDBACK
} from "../redux/AppTypes";
import service from "./EngineerService";
import {
  EngineerAction as BaseAction,
  FETCH_ENGINEERS as FETCH,
  LoadEngineersAction as LoadAction,
  LOAD_ENGINEERS as LOAD,
  PERSIST_ENGINEER as PERSIST,
  PersistEngineerResult,
  REMOVE_ENGINEER as REMOVE,
  REMOVE_ENGINEER_LOCAL,
  SET_ENGINEER_CONFLUENCE_ACCESS,
  SET_ENGINEER_JIRA_ACCESS,
  UPSERT_ENGINEER
} from "./EngineersTypes";

const fetchMiddleware: Middleware<{}, AppState> =
  (store) => (next) => async (action: BaseAction) => {
    const result = next(action);
    if (action.type === FETCH) {
      const page = action.paginated
        ? await service.fetchPage({
            limit: action.limit,
            nextToken: action.nextToken,
          })
        : undefined;
      const items = page ? page.items : await service.fetch();
      const load: LoadAction = {
        type: LOAD,
        append: action.append,
        items,
        loadedAll: page ? !page.nextToken : true,
        nextToken: page?.nextToken,
      };
      store.dispatch(load);
      return items;
    } else {
      return result;
    }
  };

const deleteMiddleware: Middleware<{}, AppState> =
  (store) => (next) => async (action: BaseAction) => {
    const result = next(action);
    if (action.type === REMOVE) {
      await service.delete(action.id);
      store.dispatch({ type: REMOVE_ENGINEER_LOCAL, id: action.id });
      return action.id;
    } else {
      return result;
    }
  };

const persistMiddleware: Middleware<{}, AppState> =
  (store) => (next) => async (action: BaseAction) => {
    const result = next(action);
    if (action.type === PERSIST) {
      const saveResult = await service.persist(action.item);
      const item = normalizePersistResult(saveResult);
      store.dispatch({ type: UPSERT_ENGINEER, item });
      return saveResult;
    } else {
      return result;
    }
  };

function normalizePersistResult(result: PersistEngineerResult | any): any {
  return result?.item || result;
}

const setEngineerJiraAccessMiddleware: Middleware<{}, AppState> =
  (store) => (next) => async (action: BaseAction) => {
    if (action.type === SET_ENGINEER_JIRA_ACCESS) {
      const id = action.id;
      await service
        .setJiraAccess(id)
        .then(() => {
          const action: ReportFeedbackAction = {
            type: REPORT_APP_FEEDBACK,
            feedback: "Jira access granted",
          };
          store.dispatch(action);
        })
        .catch(() => {})
        .finally(() => {
          if (action.callback) {
            action.callback();
          }
        }); // errors are reported by the fetch service
    }
    return next(action);
  };

const setEngineerConfluenceAccessMiddleware: Middleware<{}, AppState> =
  (store) => (next) => async (action: BaseAction) => {
    if (action.type === SET_ENGINEER_CONFLUENCE_ACCESS) {
      const id = action.id;
      await service
        .setConfluenceAccess(id)
        .then(() => {
          const action: ReportFeedbackAction = {
            type: REPORT_APP_FEEDBACK,
            feedback: "Confluence access granted",
          };
          store.dispatch(action);
        })
        .catch(() => {}); // errors are reported by the fetch service
    }
    return next(action);
  };

const middlewares = [
  fetchMiddleware,
  deleteMiddleware,
  persistMiddleware,
  setEngineerJiraAccessMiddleware,
  setEngineerConfluenceAccessMiddleware,
];
export default middlewares;
